import { apiRoutes } from '@/config/api-routes'
import { companiesApi } from '@/lib/axios'
import { Culture } from '@/types/culture'
import { Treatment } from '@/types/treatment'

export const PART_SIZE = 50 * 1024 * 1024 // 50MB per part (S3 min part size is 5MB)

export interface UploadPartItem {
  PartNumber: number
  ETag: string
}

export interface UploadResumableFileOptions {
  treatment: Treatment | string
  culture: Culture | string
  diagnosisId: string
  file: File
  fileType?: string
  options?: {
    onUploadProgress?: (progress: number) => void
    signal?: AbortSignal
  }
}

export async function uploadResumableFile({
  treatment,
  culture,
  diagnosisId,
  file,
  fileType = 'mosaic',
  options,
}: UploadResumableFileOptions) {
  // Step 1: Start Session
  const { data: startData } = await companiesApi.post<{
    sessionId: string
    fileReference: string
  }>(
    apiRoutes.uploadSessionStart(treatment, culture, diagnosisId),
    {
      fileType,
    },
    { signal: options?.signal },
  )

  const { sessionId, fileReference } = startData

  const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE))
  const parts: UploadPartItem[] = []

  // Step 2: Upload Parts
  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    if (options?.signal?.aborted) {
      throw new Error('Upload aborted')
    }

    const start = (partNumber - 1) * PART_SIZE
    const end = Math.min(start + PART_SIZE, file.size)
    const chunk = file.slice(start, end)

    // Request presigned URL for this chunk
    const { data: partData } = await companiesApi.post<{ url: string }>(
      apiRoutes.uploadSessionPart(treatment, culture, diagnosisId),
      {
        sessionId,
        fileReference,
        partNumber,
      },
      { signal: options?.signal },
    )

    // Upload directly to S3
    const s3Response = await fetch(partData.url, {
      method: 'PUT',
      body: chunk,
      signal: options?.signal,
    })

    if (!s3Response.ok) {
      throw new Error(`Failed to upload part ${partNumber}`)
    }

    // Capture ETag header (strip surrounding double quotes if present)
    const etag = s3Response.headers.get('ETag')?.replace(/"/g, '')
    if (!etag) {
      throw new Error(
        'ETag header missing. Ensure CORS ExposeHeaders includes ETag.',
      )
    }

    parts.push({ PartNumber: partNumber, ETag: etag })
    options?.onUploadProgress?.((partNumber * 100) / totalParts)
  }

  // Step 3: Finish Session
  const { data: finishData } = await companiesApi.post(
    apiRoutes.uploadSessionFinish(treatment, culture, diagnosisId),
    {
      sessionId,
      fileReference,
      parts,
    },
    { signal: options?.signal },
  )

  return finishData
}
